/**
 * Learn: the page the sidebar's Learn heading and the phone's Learn tab open.
 *
 * Coleman's layout, 2026-10-07, picked from the design canvas:
 *
 *   1. A banner for the newest thing added (a masterclass, a Q&A or an X&O
 *      video) on the coach's first two visits after it lands, unless they
 *      open it or wave it off first. Concepts are never the banner; a new
 *      one is a chip on the Lab card. Nothing new, no banner.
 *   2. The four places as tall photo tiles: Masterclasses, Insights, X&Os,
 *      Resources. Each tile's photo IS its newest cover, so the tiles change
 *      as content lands. Covers are dimmed (bam-learn-dim) so a rough frame
 *      reads as texture behind the title rather than as the picture. A small gold "N new" sits on any with something
 *      added since the coach's last visit.
 *   3. Underneath: today's Lab session (the three beats, over the gym) and
 *      the masterclasses they started and didn't finish.
 *
 * Each place's one line is its platformMap entry, so the page can't drift
 * from the tour and the empty states.
 */
import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { GOLD } from "../constants/theme";
import { PLATFORM_BY_ID } from "../constants/platformMap";
import { muxFrame } from "../masterclass/coverArt";
import { fetchPodcastFeed } from "../utils/podcast";
import { supabase } from "../../supabaseClient";
import { excludeHeld } from "../constants/collections";
import { useWatched } from "../hooks/useWatched";
import { visibleConcepts, conceptSet } from "../concepts";
import { ConceptLibrary } from "../concepts/ConceptLibrary";
import { loadConceptReads, loadReleasedConcepts } from "../concepts/conceptReads";
import DropCard from "../drops/DropCard";
import { loadLatestDrop } from "../drops/dropsApi";
import { currentDrop } from "../drops/dropModel";

export const LEARN_HOME_ID = "learn-home";
const hf = "'Bebas Neue', sans-serif";
const GYM_COURT = "/images/learn/gym-court.jpg";
const GYM_FLAGS = "/images/learn/gym-flags.jpg";

/** The newest published video in a section, for a cover. null if none. */
async function latestVideo(section) {
  try {
    // Held drills are live only on their collection page, so they don't
    // get to be a section's cover either.
    const q = supabase.from("drills")
      .select("id, title, mux_playback_id, created_at")
      .eq("section", section)
      .or("status.eq.published,status.is.null")
      .not("mux_playback_id", "is", null);
    const { data } = await excludeHeld(q)
      .order("created_at", { ascending: false })
      .limit(1);
    const row = data && data[0];
    return row ? { id: row.id, section, title: row.title, date: row.created_at,
      art: [muxFrame(row.mux_playback_id, { width: 900, height: 1200, time: 3 })] } : null;
  } catch { return null; }
}

/** The same Mux frame, asked for at banner size: a cover fetched for a card
 *  is 640 wide and goes soft across the full width of the page. */
export function bannerSize(srcs) {
  return (srcs || []).map(u => (typeof u === "string" && u.includes("image.mux.com"))
    ? u.replace(/width=\d+/, "width=1600").replace(/height=\d+/, "height=900")
    : u);
}

/** A Weekly Q&A episode's own frame, or nothing. */
function qaFrame(qa, size) {
  const id = qa && (qa.muxPlaybackId || qa.mux_playback_id);
  return id ? muxFrame(id, { ...size, time: 5 }) : null;
}

const HIDDEN_KEY = "bam_learn_banner_hidden";
/** X&O videos opened from the banner. Watching past halfway anywhere is
 *  already in drill_views; this covers the open itself. */
const OPENED_KEY = "bam_learn_banner_opened";
/** How many separate visits to Learn have shown each banner. A new thing
 *  is the banner on the first two visits after it lands, then it's done:
 *  the news is for when it's new (Coleman, 2026-10-07). Two rather than
 *  one, because the first visit is often a pass-through on the way
 *  somewhere else. */
const VIEWS_KEY = "bam_learn_banner_views";
export const BANNER_VISITS = 2;
function readBannerViews() {
  try { const v = JSON.parse(localStorage.getItem(VIEWS_KEY) || "{}"); return v && typeof v === "object" ? v : {}; }
  catch { return {}; }
}
function readOpenedBanners() {
  try { const v = JSON.parse(localStorage.getItem(OPENED_KEY) || "[]"); return Array.isArray(v) ? v : []; }
  catch { return []; }
}
function readHiddenBanners() {
  try { const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]"); return Array.isArray(v) ? v : []; }
  catch { return []; }
}

/** Resources rotates, one kind a day, in this order. */
export const RESOURCE_TURNS = ["qa", "podcast", "research"];
/** Today's kind: the day's turn, or the next one along if that kind is
 *  known to be missing (null). Still loading (undefined) keeps its turn. */
export function resourceTurn(day, available) {
  const n = RESOURCE_TURNS.length;
  const start = ((day % n) + n) % n;
  for (let k = 0; k < n; k++) {
    const kind = RESOURCE_TURNS[(start + k) % n];
    if (available[kind] !== null) return kind;
  }
  return null;
}

/** The concept added most recently, and whether this coach has opened it.
 *  `reads` null means not loaded yet: counted as opened, so the banner waits
 *  rather than flashing something older first. */
export function newestConcept(reads, released = {}) {
  const c = visibleConcepts(released).filter(x => x.added)
    .sort((a, b) => String(b.added).localeCompare(String(a.added)))[0];
  if (!c) return null;
  return { ...c, date: c.added, opened: reads == null ? true : !!reads[c.key] };
}

/** When a dated thing was added, as a time; NaN when it can't be read. */
const when = (d) => new Date(d && String(d).length === 10 ? `${d}T12:00:00` : d).getTime();

/**
 * What the banner announces (Coleman, 2026-10-07): the one thing we added
 * most recently, a masterclass, a Q&A or an X&O video, until either we add
 * something newer or this coach opens it once. There's no time limit, and
 * opening it doesn't hand the banner to something older: it was the news,
 * and they've seen it. Concepts are never the banner; they're marked in the
 * Lab and on the Lab card instead.
 *
 * Each argument is that kind's newest item with `date` and `opened`, or
 * null. Returns the banner, or null.
 */
export function learnFeature({ masterclass, qa, xo }) {
  const cands = [
    masterclass && masterclass.title && { kind: "masterclass", item: masterclass },
    qa && qa.title && { kind: "qa", item: qa },
    xo && xo.title && { kind: "xo", item: xo },
  ].filter(Boolean).filter(c => Number.isFinite(when(c.item.date)));
  if (!cands.length) return null;
  const top = cands.sort((x, y) => when(y.item.date) - when(x.item.date))[0];
  if (top.item.opened) return null;
  const it = top.item;
  // Every banner picture is the thing itself: the masterclass's cover, the
  // episode's own frame, the video's own frame. The gym only stands in for
  // what has no video.
  if (top.kind === "masterclass") {
    return { kind: "masterclass", key: it.key, id: `mc:${it.key || it.title}`, eyebrow: "New masterclass", title: it.title,
      sub: it.sub || PLATFORM_BY_ID.master?.what, art: [...bannerSize(it.art), GYM_FLAGS],
      cta: "Start reading", more: "All masterclasses", moreTo: "master" };
  }
  if (top.kind === "qa") {
    return { kind: "qa", id: `qa:${it.id || it.title}`, eyebrow: "New Q&A", title: it.title, sub: "This week's questions, answered.",
      art: [qaFrame(it, { width: 1600, height: 900 }), GYM_FLAGS], cta: "Read it", more: "Resources", moreTo: "resources" };
  }
  return { kind: "xo", key: it.id, id: `xo:${it.id || it.title}`, eyebrow: "New X&O", title: it.title,
    sub: PLATFORM_BY_ID.xo?.what, art: [...bannerSize(it.art), GYM_FLAGS],
    cta: "Watch it", more: "All X&Os", moreTo: "xo" };
}

/** An image that tries each source in turn, and draws nothing if all fail. */
function Cover({ srcs, alt = "", style, className = "" }) {
  const list = (srcs || []).filter(Boolean);
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); }, [list.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  if (i >= list.length) return null;
  return <img src={list[i]} alt={alt} loading="lazy" onError={() => setI(n => n + 1)} className={`bam-learn-art ${className}`.trim()}
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", ...style }}/>;
}

function Ring({ pct, size = 32 }) {
  const r = (size - 4) / 2, c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, Number(pct) || 0));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(226,221,159,0.18)" strokeWidth="3"/>
      <circle className="bam-learn-ring" cx={size / 2} cy={size / 2} r={r} fill="none" stroke={GOLD} strokeWidth="3"
        strokeLinecap="round" strokeDasharray={`${c * p} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}/>
    </svg>
  );
}

/** A section's entrance: fade and rise, one after the other. */
const enter = (i) => ({ animation: "bamLearnIn .5s cubic-bezier(.22,.8,.24,1) both", animationDelay: `${i * 70}ms` });
const shade = "linear-gradient(180deg, rgba(0,0,0,0.04) 28%, rgba(0,0,0,0.88) 100%)";

export function LearnHome({ C, dark, go, labOpen, recentMasterclasses = [], newestMasterclass = null, freshMasterclassCount = 0,
  resumable = [], latestQa, qaOpened = true, qaIsNew = false, onOpenQa, onOpenModule, newCounts = {}, day }) {
  const [lab, setLab] = useState(null);       // { done, sessions, week }
  const [xo, setXo] = useState(null);
  const [insight, setInsight] = useState(null);
  const [concept, setConcept] = useState(null);
  // Every concept with read state, for "Learn the science" beside today's
  // session. Unread until the reads load, which is what the Lab shows too.
  const [concepts, setConcepts] = useState(() => conceptSet({}, 99));
  // This week's Wednesday Drop: Learn pairs it, weekly, with the Lab, daily.
  const [weeklyDrop, setWeeklyDrop] = useState(null);
  useEffect(() => {
    let alive = true;
    loadLatestDrop().then(d => { if (alive) setWeeklyDrop(d && currentDrop([d]) ? d : null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // undefined while loading, null when there is none.
  const [podcast, setPodcast] = useState(undefined);
  const [research, setResearch] = useState(undefined);

  const today = Number.isFinite(day) ? day : Math.floor(Date.now() / 86400000);
  const turn = resourceTurn(today, { qa: latestQa || null, podcast, research });

  useEffect(() => {
    let alive = true;
    latestVideo("xo").then(v => { if (alive) setXo(v); });
    latestVideo("insights").then(v => { if (alive) setInsight(v); });
    Promise.all([loadConceptReads(), loadReleasedConcepts()]).then(([r, rel]) => {
      if (!alive) return;
      setConcept(newestConcept(r || {}, rel));
      setConcepts(conceptSet(r || {}, 99, rel));
    }).catch(() => {});
    if (labOpen) {
      // Both are the Lab's own reads; a coach who can't use the Lab yet gets
      // a permission error, which just leaves the card without numbers.
      let tz = "UTC";
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { /* old browser */ }
      Promise.all([
        supabase.rpc("lab_stats"),
        supabase.rpc("lab_lane_availability", { p_tz: tz }),
      ]).then(([s, a]) => {
        if (!alive) return;
        setLab({
          sessions: Number(s?.data?.sessions) || 0,
          week: Number(s?.data?.reps_week) || 0,
          done: !!a?.data?.done_recently,
        });
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [labOpen]);

  // Only fetch what today's Resources turn needs (the podcast feed is a round
  // trip through a proxy). A miss marks it null and the turn moves along.
  useEffect(() => {
    let alive = true;
    if (turn === "podcast" && podcast === undefined) {
      fetchPodcastFeed().then(({ podcast: show, episodes }) => {
        if (!alive) return;
        const ep = episodes && episodes[0];
        setPodcast(ep ? { title: ep.title, art: [ep.image || show?.image] } : null);
      }).catch(() => { if (alive) setPodcast(null); });
    }
    if (turn === "research" && research === undefined) {
      Promise.resolve(supabase.rpc("research_collection_index")).then(({ data }) => {
        if (!alive) return;
        const c = (Array.isArray(data) ? data : []).find(x => x && Number(x.study_count) > 0);
        setResearch(c ? { title: c.title, count: Number(c.study_count), art: [c.cover_url] } : null);
      }).catch(() => { if (alive) setResearch(null); });
    }
    return () => { alive = false; };
  }, [turn, podcast, research]);

  // A coach can wave the banner off; it stays off for that item, and the
  // next new thing brings it back.
  const [hidden, setHidden] = useState(() => readHiddenBanners());
  const { watched, loading: watchedLoading } = useWatched();
  const announced = learnFeature({
    masterclass: newestMasterclass ? { ...newestMasterclass, date: newestMasterclass.addedAt } : null,
    qa: latestQa ? { ...latestQa, date: latestQa.published_at || latestQa.created_at || (latestQa.week_of && `${latestQa.week_of}T12:00:00`), opened: qaOpened } : null,
    // Counted as opened until the watch history loads, so nothing older flashes up first.
    xo: xo ? { ...xo, opened: watchedLoading || watched.has(xo.id) || readOpenedBanners().includes(`xo:${xo.id}`) } : null,
  });
  // Visits counted per banner, once per visit: the count as this visit
  // found it decides, so the banner doesn't vanish mid-visit.
  const viewsAtVisit = useRef(null);
  if (viewsAtVisit.current === null) viewsAtVisit.current = readBannerViews();
  const counted = useRef(new Set());
  useEffect(() => {
    const id = announced && announced.id;
    if (!id || counted.current.has(id)) return;
    counted.current.add(id);
    const v = readBannerViews();
    v[id] = (Number(v[id]) || 0) + 1;
    const keep = Object.fromEntries(Object.entries(v).slice(-40));
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(keep)); } catch { /* private window */ }
  }, [announced && announced.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const fresh = announced && (Number(viewsAtVisit.current[announced.id]) || 0) < BANNER_VISITS;
  const feature = announced && fresh && !hidden.includes(announced.id) ? announced : null;
  const hideFeature = () => {
    if (!feature) return;
    const next = [...hidden.filter(x => x !== feature.id), feature.id].slice(-30);
    setHidden(next);
    try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(next)); } catch { /* private window */ }
  };
  // The Masterclasses tile never repeats the banner: when the newest one is
  // up there, the tile shows the one before it.
  const tileMasterclass = (recentMasterclasses || []).find(m =>
    !(feature && feature.kind === "masterclass" && m.key === feature.key)) || null;
  const openFeature = () => {
    if (!feature) return;
    if (feature.kind === "masterclass" && feature.key && onOpenModule) onOpenModule(feature.key);
    else if (feature.kind === "qa" && onOpenQa) onOpenQa();
    else if (feature.kind === "xo" && feature.key) {
      try {
        localStorage.setItem(OPENED_KEY, JSON.stringify([...readOpenedBanners(), feature.id].slice(-30)));
        sessionStorage.setItem("bam_open_drill", JSON.stringify({ sectionId: "xo", id: feature.key }));
      } catch { /* private window */ }
      go("xo");
    }
    else go(feature.moreTo);
  };

  const resource = turn === "podcast" && podcast ? { line: `New episode: ${podcast.title}`, art: podcast.art }
    : turn === "research" && research ? { line: `${research.count} studies: ${research.title}`, art: research.art }
    : turn === "qa" && latestQa ? { line: `This week's Q&A: ${latestQa.title}`, art: [qaFrame(latestQa, { width: 900, height: 1200 })] }
    : null;

  const tiles = [
    { id: "master", label: "Masterclasses", art: tileMasterclass?.art,
      line: tileMasterclass?.title && `${tileMasterclass.key === (recentMasterclasses[0] || {}).key ? "Newest" : "Recent"}: ${tileMasterclass.title}`,
      fresh: freshMasterclassCount },
    { id: "insights", label: "Insights", art: insight?.art, line: insight?.title && `Latest: ${insight.title}`,
      fresh: (Number(newCounts.insights) || 0) + (Number(newCounts.ti) || 0) },
    { id: "xo", label: "X&Os", art: xo?.art, line: xo?.title && `Latest: ${xo.title}`, fresh: Number(newCounts.xo) || 0 },
    // Falls back to the gym only if today's turn has no picture at all.
    { id: "resources", label: "Resources", art: [...(resource?.art || []), GYM_FLAGS], line: resource?.line,
      fresh: qaIsNew ? 1 : 0 },
  ];
  const picks = (resumable || []).slice(0, 3);
  const card = {
    background: C.cardGradient || (dark ? "#121212" : "#FFFFFF"),
    border: `1px solid ${C.cardBorder || C.border}`,
    boxShadow: [C.surfaceShadow, C.innerGlow].filter(Boolean).join(", ") || "none",
    borderRadius: 16, color: C.text,
  };

  return (
    <div className="bam-content-scroll" style={{ flex: 1, overflow: "auto", padding: feature ? 0 : 28 }}>
      {/* The page names itself (Coleman, 2026-10-08), in the display face its
          places use. Above the banner when there is one, so it's always the
          first thing on the page. */}
      <h1 className="bam-learn-title" style={{ margin: 0, padding: feature ? "28px 28px 18px" : "0 0 18px",
        fontFamily: hf, fontWeight: 400, fontSize: 56, letterSpacing: 1.6, lineHeight: 1, color: C.text, ...enter(0) }}>
        Learn
      </h1>
      {feature && (
        <section className="bam-learn-feature" aria-label={feature.eyebrow}
          style={{ position: "relative", height: 400, overflow: "hidden", background: "#000", ...enter(0) }}>
          <Cover srcs={feature.art} className="bam-learn-dim" style={{ objectPosition: "center 30%" }}/>
          <div aria-hidden="true" style={{ position: "absolute", inset: 0,
            background: "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.72) 40%, rgba(10,10,10,0.1) 78%), linear-gradient(180deg, rgba(10,10,10,0) 58%, #0A0A0A 100%)" }}/>
          <div className="bam-learn-feature-copy" style={{ position: "absolute", left: 40, bottom: 52, width: "min(580px, calc(100% - 80px))",
            display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase", color: GOLD }}>
              Just dropped · {feature.eyebrow}
            </span>
            <h2 style={{ margin: 0, fontFamily: hf, fontWeight: 400, fontSize: 68, letterSpacing: 1.5, lineHeight: .92,
              color: "#FFFFFF", textWrap: "balance" }}>{feature.title}</h2>
            {feature.sub && <p style={{ margin: 0, fontSize: 15, color: "#C9C9C9", lineHeight: 1.5 }}>{feature.sub}</p>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
              <button type="button" className="btn" onClick={openFeature}
                style={{ minHeight: 46, padding: "0 22px", borderRadius: 23, border: "none", background: GOLD, color: "#111",
                  fontFamily: "inherit", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>{feature.cta}</button>
              <button type="button" className="btn" onClick={() => go(feature.moreTo)}
                style={{ minHeight: 46, padding: "0 22px", borderRadius: 23, border: "1px solid rgba(255,255,255,0.25)",
                  background: "transparent", color: "#F5F5F5", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{feature.more}</button>
              <button type="button" className="btn bam-learn-feature-hide" onClick={hideFeature}
                style={{ minHeight: 46, padding: "0 14px", borderRadius: 23, border: "none", background: "transparent",
                  color: "rgba(255,255,255,0.6)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Got it</button>
            </div>
          </div>
        </section>
      )}

      <div className="bam-learn-body" style={{ display: "flex", flexDirection: "column", gap: 20,
        padding: feature ? "0 28px 28px" : 0, marginTop: feature ? -14 : 0, position: "relative" }}>
        {/* The four places. Each photo is its newest cover. */}
        <div className="bam-learn-tiles" style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(4, minmax(0, 1fr))", ...enter(1) }}>
          {tiles.map(t => (
            <button key={t.id} type="button" className="btn cc" onClick={() => go(t.id)}
              aria-label={`${t.label}${t.fresh > 0 ? `, ${t.fresh} new` : ""}`}
              style={{ position: "relative", height: 330, padding: 0, borderRadius: 16, overflow: "hidden", cursor: "pointer",
                border: `1px solid ${C.cardBorder || C.border}`, background: dark ? "#111" : "#1A1A1A", textAlign: "left", fontFamily: "inherit" }}>
              <Cover srcs={t.art} className="bam-learn-dim"/>
              <span aria-hidden="true" style={{ position: "absolute", inset: 0, background: shade }}/>
              {t.fresh > 0 && (
                <span className="bam-learn-new" style={{ position: "absolute", top: 12, left: 12, fontSize: 10, fontWeight: 800,
                  letterSpacing: .6, textTransform: "uppercase", color: "#111", background: GOLD, padding: "4px 9px", borderRadius: 10,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.35)" }}>
                  {t.fresh > 9 ? "9+" : t.fresh} new
                </span>
              )}
              <span className="bam-learn-tile-copy" style={{ position: "absolute", left: 18, right: 18, bottom: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, fontFamily: hf, fontSize: 38, letterSpacing: 1.4, lineHeight: 1, color: "#FFFFFF" }}>{t.label}</span>
                  <ArrowRight size={18} color="rgba(255,255,255,0.75)" className="bam-learn-arrow"/>
                </span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.82)", lineHeight: 1.4,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {t.line || PLATFORM_BY_ID[t.id]?.what}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* Today's session, the science beside it (Coleman, 2026-10-08:
            the Lab's "Learn the science", copied here), and anything half
            read. Three across on a wide screen; the half-read list wraps
            under the other two when it's narrower (dashboard.css). */}
        {weeklyDrop && (
          <div style={{ ...enter(2) }}>
            <DropCard C={C} drop={weeklyDrop} go={go} eyebrow="Weekly"/>
          </div>
        )}

        {(labOpen || picks.length > 0) && (
          <div className={`bam-learn-today${labOpen && picks.length ? " bam-learn-today-3" : ""}`} style={{ display: "grid", gap: 16,
            gridTemplateColumns: labOpen
              ? (picks.length ? "minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1.25fr) minmax(0, 1fr)")
              : "minmax(0, 1fr)", ...enter(2) }}>
            {labOpen && (
              <div style={{ position: "relative", minWidth: 0 }}>
              {/* A concept the coach hasn't opened yet: not a banner (Learn's
                  banner is for masterclasses, Q&As and X&Os), a mark on the
                  Lab, which is where concepts live. It stays until they open
                  it, and it opens the concept itself rather than a session. */}
              {concept && !concept.opened && (
                <button type="button" className="btn bam-learn-concept"
                  onClick={() => {
                    try { sessionStorage.setItem("bam_open_concept", concept.key); } catch { /* private window */ }
                    go("learn");
                  }}
                  aria-label={`New concept: ${concept.title}. Read it`}
                  style={{ position: "absolute", top: 18, right: 18, zIndex: 2, maxWidth: "calc(100% - 36px)",
                    display: "inline-flex", alignItems: "center", gap: 8, minHeight: 36, padding: "0 14px",
                    borderRadius: 18, border: `1px solid ${GOLD}88`, background: "rgba(10,10,10,0.72)",
                    backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                    color: "#F5F5F5", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, background: GOLD, flexShrink: 0 }}/>
                  <span style={{ color: GOLD, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, flexShrink: 0 }}>NEW CONCEPT</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{concept.title}</span>
                  <ArrowRight size={13} style={{ flexShrink: 0 }}/>
                </button>
              )}
              <button type="button" className="btn cc bam-learn-lab" onClick={() => go("learn")}
                style={{ position: "relative", display: "block", width: "100%", height: "100%", minHeight: 300, padding: 0, borderRadius: 16, overflow: "hidden", cursor: "pointer",
                  border: `1px solid ${GOLD}55`, background: "#000", textAlign: "left", fontFamily: "inherit", color: "#F5F5F5" }}>
                <Cover srcs={[GYM_COURT]} style={{ objectPosition: "center 60%" }}/>
                <span aria-hidden="true" style={{ position: "absolute", inset: 0,
                  background: "linear-gradient(90deg, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.78) 50%, rgba(10,10,10,0.35) 100%)" }}/>
                <span style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "space-between",
                  gap: 18, padding: "26px 28px", minHeight: 300, boxSizing: "border-box" }} className="bam-learn-lab-copy">
                  <span style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, color: GOLD }}>
                      {lab?.done ? "DAILY · LEARNING LAB · DONE FOR TODAY" : "DAILY · LEARNING LAB · TODAY"}
                    </span>
                    <span style={{ fontFamily: hf, fontSize: 52, letterSpacing: 1.5, lineHeight: .92 }}>Today's session</span>
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                    {["Watch one thing", "Work a lane", "Answer one challenge"].map((b, i) => (
                      <span key={b} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, fontWeight: 600,
                        color: i === 0 ? "#F5F5F5" : "#C9C9C9" }}>
                        <span style={{ width: 26, height: 26, borderRadius: 13, flexShrink: 0, display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 12, fontWeight: 800,
                          background: i === 0 ? GOLD : "transparent", color: i === 0 ? "#111" : GOLD,
                          border: i === 0 ? "none" : `1px solid ${GOLD}80` }}>{i + 1}</span>
                        {b}
                      </span>
                    ))}
                  </span>
                  <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
                    <span className="bam-learn-go" style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 44,
                      padding: "0 20px", borderRadius: 22, background: GOLD, color: "#111", fontSize: 14, fontWeight: 800 }}>
                      {lab?.done ? "Open the Lab" : "Start today's session"} <ArrowRight size={15} className="bam-learn-arrow"/>
                    </span>
                    {lab && (lab.sessions > 0 || lab.week > 0) && (
                      <span style={{ fontSize: 13, color: "#C9C9C9" }}>
                        {[lab.sessions > 0 && `${lab.sessions} session${lab.sessions === 1 ? "" : "s"} done`,
                          lab.week > 0 && `${lab.week} rep${lab.week === 1 ? "" : "s"} this week`].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                </span>
              </button>
              </div>
            )}

            {labOpen && concepts.length > 0 && (
              <section aria-label="Learn the science" style={{ ...card, padding: "20px 22px", minWidth: 0 }}>
                <ConceptLibrary C={C} concepts={concepts} bare onOpen={(c) => {
                  try { sessionStorage.setItem("bam_open_concept", c.key); } catch { /* private window */ }
                  go("learn");
                }}/>
              </section>
            )}

            {picks.length > 0 && (
              <section aria-labelledby="learn-pickup" style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 2 }}>
                <span id="learn-pickup" style={{ fontFamily: hf, fontSize: 25, letterSpacing: 1, marginBottom: 6 }}>Pick up where you left off</span>
                {picks.map((p, i) => {
                  const left = Number(p.pagesLeft) || 0, read = Number(p.pagesRead) || 0;
                  return (
                    <button key={p.key} type="button" className="btn bam-learn-pick" onClick={() => onOpenModule && onOpenModule(p.moduleKey || p.key)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", border: "none", cursor: "pointer",
                        borderBottom: i < picks.length - 1 ? `1px solid ${C.cardBorder || C.border}` : "none",
                        background: "transparent", color: C.text, textAlign: "left", fontFamily: "inherit" }}>
                      <span style={{ position: "relative", width: 72, height: 46, borderRadius: 8, overflow: "hidden",
                        background: "#000", flexShrink: 0 }}><Cover srcs={p.art}/></span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 14, fontWeight: 700, lineHeight: 1.3,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                        <span style={{ display: "block", fontSize: 12, color: C.textDim, marginTop: 3 }}>
                          {left === 1 ? "One page left" : `${left} pages left`}
                        </span>
                      </span>
                      <Ring pct={read + left > 0 ? read / (read + left) : 0}/>
                    </button>
                  );
                })}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LearnHome;
